import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Redirect } from "react-router-dom";
import firebase from "services/firebase/firebase.config";
import { useCompanyStore } from "web-apps/company/stores/companyStore/companyStore";
import { useAuthStore } from "stores/authStore/authStore";
import { Color } from "config/colors";
import { FontSize } from "config/font";
import { ButtonStyled } from "components/buttons/buttons.styled";

import { Text } from "components/Typography/text.styled";
import {
  TextInput,
  LabelStyled,
  InputContainer,
} from "components/form/inputs/inputs.styled";
import { FormStyled } from "./loginForm.styled";
import { CircularProgress } from "@material-ui/core";
import { CompanyRole } from "model/User";

type Props = {
  email: string;
  setEmail: React.Dispatch<React.SetStateAction<string>>;
};

export const LoginForm: React.FC<Props> = ({ email, setEmail }) => {
  const { t } = useTranslation();
  const [authState, , authDispatch] = useAuthStore();
  const [companyState] = useCompanyStore();
  const [password, setPassword] = useState("");
  const [ableToLogIn, setAbleToLogIn] = useState(false);
  const [errorMessage, setErrorMessage] = useState(false);

  useEffect(() => {
    if (email && password) {
      setEmail(email.trim());
      setAbleToLogIn(true);
    } else {
      setAbleToLogIn(false);
    }
  }, [email, password]);

  const handleLogin = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (ableToLogIn) {
      authDispatch({ type: "SET_IS_LOADING", payload: true });
      await firebase
        .auth()
        .signInWithEmailAndPassword(email, password)
        .catch(() => {
          setErrorMessage(true);
          authDispatch({ type: "SET_IS_LOADING", payload: false });
        });
    }
  };

  return (
    <>
      {authState.signedIn && companyState.companies.length > 0 && (
        <Redirect
          to={
            companyState.companies.length > 1
              ? "./company-select"
              : authState.user?.role !== CompanyRole.COMPANY_TIMESTAMP ? "./calendar" : "./stampclock"
          }
        />
      )}
      <FormStyled onSubmit={handleLogin}>
        <InputContainer>
          <LabelStyled>{t("LoginCompany.Email")}</LabelStyled>
          <TextInput value={email} onChange={(e) => setEmail(e.target.value)} />
        </InputContainer>
        <InputContainer>
          <LabelStyled>{t("LoginCompany.Password")}</LabelStyled>
          <TextInput
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </InputContainer>
        {errorMessage && (
          <Text color={Color.Destructive} fontSize={FontSize.Small}>
            {t("LoginCompany.EmailPasswordCombination")}
          </Text>
        )}
        <ButtonStyled
          style={{ marginTop: "40px" }}
          disabled={!ableToLogIn}
          icon={authState.isLoading}
        >
          {authState.isLoading ? (
            <CircularProgress size={18} color="inherit" />
          ) : (
            t("LoginCompany.Login")
          )}
        </ButtonStyled>
      </FormStyled>
    </>
  );
};
